import React, { useCallback, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Button, Checkbox, Divider, Dropdown, Empty, Input, Pagination, Radio, Skeleton, Space, Table, Tooltip } from 'antd';
import { CaretDownOutlined, LeftOutlined, LoadingOutlined, LockOutlined, QuestionCircleOutlined, RightOutlined, UnlockOutlined } from '@ant-design/icons';
import { DndContext, DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import useSearch from '../../gql/hooks/useSearch';
import classNames from 'classnames';
import { ColoredTag } from '../../pages/GenericCreateEdit/GenericElements';
import { getFileTypeSpecs } from '../../utils';
import './styles.less';

// react-dnd item type for reordering rows within the selected pane.
const ROW_TYPE = 'selector-table-row';

function dedupe( items, accessorFn = item => item ) {

    const ids = Array.from( new Set( items.map( accessorFn ) ) );
    return ids.map( id => items.find( item => accessorFn( item ) === id ) ).filter( i => !!i );
}

/**
 * A drag-to-reorder antd table row (used only by the selected pane). `id`,
 * `index`, `canDrag` and `moveRow` are injected via the Table's `onRow` and
 * destructured out here so they never reach the <tr>. Reordering is keyed on
 * `id` (not the display index, which is filtered/paged) and commits on drop.
 * `canDrag` gates it per row so hidden rows can't be dragged or dropped onto.
 * `index` is used only to pick the drop indicator direction.
 */
const DraggableRow = ( { id, index, canDrag = true, moveRow, className, ...restProps } ) => {
    const ref = useRef( null );
    const [ { isOver, dropClassName }, drop ] = useDrop( {
        accept: ROW_TYPE,
        canDrop: () => canDrag,
        collect: monitor => {
            const dragged = monitor.getItem();
            if ( !dragged || dragged.id === id || !monitor.canDrop() ) {
                return {};
            }
            return {
                isOver: monitor.isOver(),
                dropClassName: dragged.index < index ? 'drop-over-downward' : 'drop-over-upward',
            };
        },
        drop: item => moveRow( item.id, id ),
    } );
    const [ { isDragging }, drag ] = useDrag( {
        item: { type: ROW_TYPE, id, index },
        canDrag: () => canDrag,
        collect: monitor => ( { isDragging: monitor.isDragging() } ),
    } );
    drop( drag( ref ) );
    return (
        <tr
            ref={ ref }
            className={ classNames(
                className,
                { 'selector-table-draggable-row': canDrag, 'is-dragging': isDragging },
                isOver && dropClassName,
            ) }
            { ...restProps }
        />
    );
};

const DEFAULT_VIEW = 'default';

/**
 * One side of the transfer (available or selected). Single-column antd Table
 * whose cell is produced by `renderRow`, with controlled checkbox selection and
 * a whole-pane drop target that accepts rows dragged from the OTHER side.
 */
export const SelectorPane = ( { title, items, renderRow, disabled, loading, controls, hideable, hideHidden, canViewCurrent = false,
    checkedItems = [], uncheckableIds = [], disabledIds = [], 
    onCheckedChange, draggable = false, moveRow, onHiddenChange, additionalColumns = [],  footer,
    onTableChange: _onTableChange, style, className } ) => {

    const placeholderItems = useMemo( () => new Array( 10 ).fill( { placeholder: true, loading: true } ).map( ( item, i ) => ( { ...item, id: i, width: Math.random() * 70 + 30 } ) ), [] );

    const displayedItems = useMemo( () => {
        if ( loading ) {
            return placeholderItems;
        }

        const sorted = [ ...items ].sort( ( a, b ) => ( a.isEnabled === false ) - ( b.isEnabled === false ) );

        if ( hideHidden || !hideable ) {
            return sorted.filter( i => i.isEnabled !== false );
        }

        if ( items.some( i => i.isEnabled === false ) && items.some( i => i.isEnabled !== false ) ) {
            sorted.splice( sorted.findIndex( i => i.isEnabled === false ), 0, { placeholder: true, name: 'Disabled' } );
        }

        return sorted;
    }, [ items, loading, placeholderItems, hideHidden, hideable ] );

    const columns = useMemo( () => 
        [ 
            {
                key: 'row',
                title,
                render: ( _, item ) => ( renderRow ? renderRow( item ) : item.loading ? <Skeleton active style={ { width: `${ item.width }%` } } key={ item.id } paragraph={ null } /> : item.name ),
            }, 
            ...additionalColumns, 
            ( hideable && !hideHidden && {
                key: 'hidden',
                dataIndex: 'isEnabled',
                width: 'min-content',
                align: 'center',
                title: <><span>Disable</span><Tooltip title='Objects that have been published on this course can not be removed - only disabled'><QuestionCircleOutlined /></Tooltip></>,
                className: 'hidden-column',
                render: ( isEnabled, item ) => item.placeholder ? <></> :
                    <Button className='hide-button' disabled={ !item.isPermanent || disabled } onClick={ () => setHidden( item, !!isEnabled ) }>
                        { isEnabled ? <UnlockOutlined /> : <LockOutlined /> }
                    </Button>
            } ) 
        ].filter( i => !!i ), [ hideable, hideHidden, additionalColumns, disabled, title ] );

    function isCheckable( item ) {
        return !uncheckableIds.includes( item.id );
    }

    function isDisabled( item ) {
        return disabledIds.includes( item.id );
    }

    function setHidden( item, hidden ) {
        if ( disabled ) {return;}
        onHiddenChange?.( item, hidden );
    }

    // Clicking a row toggles its selection. We skip clicks that land on the
    // checkbox cell, which antd already toggles, to avoid a double-toggle.
    const handleRowClick = ( record ) => ( event ) => {
        if ( disabled || !isCheckable( record ) || isDisabled( record ) || record.placeholder ) {
            return;
        }
        if ( event.target.closest?.( '.ant-checkbox' ) || event.target.closest?.( '.hide-button' ) ) {
            return;
        }

        onCheckedChange?.( [ record ], !checkedItems.some( i => i.id === record.id ) );
    };

    // onRow carries the row-click handler; the selected pane also passes the
    // index + moveRow that DraggableRow needs to reorder.
    const onRow = ( record, index ) => ( {
        onClick: handleRowClick( record ),
        ...( !draggable || disabled ? {} : {
            id: record.id,
            index,
            canDrag: record.isEnabled !== false && !record.placeholder, // any non-hidden, non-placeholder row is draggable
            moveRow,
        } ),
    } );

    const rowComponents = !draggable ? {} : { 
        components: { 
            body: { 
                row: DraggableRow 
            } 
        } 
    };

    function onTableChange( ...props ) {
        if ( disabled ) {return;}
        _onTableChange?.( ...props );
    }

    return (
        <div className={ classNames( 'selector-table-pane', className ) } style={ style }>
            { controls }
            <Table
                size="small"
                rowKey="id"
                loading={ loading }
                columns={ columns }
                dataSource={ displayedItems }
                pagination={ false }
                onChange={ onTableChange }
                onRow={ onRow }
                rowClassName={ record => classNames( { 
                    'is-draggable': draggable && !disabled && record.isEnabled !== false && !record.placeholder,
                    'is-clickable': !draggable && !disabled && isCheckable( record ) && !isDisabled( record ) && !record.placeholder,
                    'disabled': disabled || isDisabled( record ) || record.isEnabled === false,
                    'hidden': record.isEnabled === false,
                    'placeholder': record.placeholder,
                    'loading-item': record.loading
                } ) }
                locale={ { emptyText: <Empty image={ Empty.PRESENTED_IMAGE_SIMPLE } description="None" /> } }
                rowSelection={ {
                    selectedRowKeys: checkedItems.map( c => c.id ),
                    onSelect: ( record, isSelected ) => onCheckedChange?.( [ record ], isSelected ),
                    onSelectAll: ( areSelected, _, records ) => onCheckedChange?.( records, areSelected ),
                    getCheckboxProps: item => ( { 
                        disabled: disabled || loading || item.placeholder || isDisabled( item ) || !isCheckable( item ),
                        styles: ( loading || item.placeholder || !isCheckable( item ) ) && { display: 'none' }
                    } ),
                    renderCell: ( checked, item, index, originNode ) => ( isCheckable( item ) ? originNode : null ),
                } }
                { ...rowComponents }
            />
            { footer && <div className='footer'>
                { footer }
            </div> }
        </div>
    );
};

const DEFAULT_PAGE_SIZE = 10;

/**
 * Fills in items the caller only holds ids for. Both ItemTable variants start from whatever they
 * were handed - usually a saved form value carrying ids and little else - and look those ids up,
 * so a parent holding only ids never has to fetch on its own.
 *
 * The lookup is display-only. What an ItemTable reports back out is always the caller's own item,
 * never the search record, so none of these fields leak into a form value.
 *
 * It runs whenever the id set changes rather than strictly on mount, because a form value usually
 * lands a tick after the field first renders, and a form reset can swap in ids it has not seen.
 */
const useHydratedItems = ( type, ids = [] ) => {

    // cache-first so the two panes of a SelectorTable, which open on the same ids, share one request
    const { searchData, loadSearch } = useSearch( { type, fetchPolicy: 'cache-first' } );

    // every record looked up so far, keyed by id. useSearch only holds its latest response, so
    // results are collected here - otherwise looking up a new id would drop the ones before it
    const [ lookup, setLookup ] = useState( {} );

    const key = ids.join( ',' );

    // look up only the ids not already found. An id whose request was overtaken by a newer one
    // is still missing, so it simply rides along with the next request
    useEffect( () => {
        const missing = ids.filter( id => !lookup[ id ] );

        if ( missing.length === 0 ) {
            return;
        }
        loadSearch( { searchIds: missing, limit: missing.length } );
    }, [ key ] );

    useEffect( () => {
        if ( searchData.length === 0 ) {
            return;
        }
        setLookup( prev => {
            const next = { ...prev };
            searchData.forEach( record => { next[ record.id ] = record; } );
            return next;
        } );
    }, [ searchData ] );

    return useCallback( ( item ) => {
        const reference = lookup[ item.id ];

        if ( !reference ) {
            return item;
        }

        // the caller's own fields win - isEnabled, sortOrder and isPermanent are local state the
        // search knows nothing about - so the lookup only fills the gaps
        const merged = { ...reference };
        Object.entries( item ).forEach( ( [ field, value ] ) => {
            if ( value !== undefined ) {
                merged[ field ] = value;
            }
        } );

        return merged;
    }, [ lookup ] );
};

/**
 * The stateful half of a selector pane: it owns one table's display state and the id lookup above,
 * and reports every change back out instead of holding the value itself.
 *
 * Not rendered directly - use ItemTable.Searchable or ItemTable.Draggable, which configure it.
 */
const ItemTable = ( { type = 'objects', items = [], hydrateIds, ...paneProps } ) => {

    const hydrate = useHydratedItems( type, hydrateIds ?? [] );

    const hydratedItems = useMemo( () => items.map( hydrate ), [ items, hydrate ] );

    return <SelectorPane { ...paneProps } items={ hydratedItems } />;
};

/**
 * Browsable table: server-side search, sort and pagination over `type`, all owned here. It is the
 * only variant that queries after the initial lookup, and the only thing that leaves it is a
 * check change.
 *
 * @param {Array} props.currentItems when given, a toggle appears that swaps the table over to these
 *  and freezes the search controls - used for "show me just what I have checked"
 * @param {Array} props.hydrateIds ids to look up on init, for items this table did not search up
 */
const Searchable = ( { type = 'objects', title = 'Available', currentItems, currentLabel = 'Selected',
    disabled = false, pageSize = DEFAULT_PAGE_SIZE, ...itemProps } ) => {

    const [ search, setSearch ] = useState( '' );
    const [ page, setPage ] = useState( 1 );
    const [ sort, setSort ] = useState( 'name' );
    const [ direction, setDirection ] = useState( 'ascend' );
    const [ viewState, setViewState ] = useState( DEFAULT_VIEW );

    const { searchData, searchDataCount, searchLoading, loadSearch } = useSearch( { type } );

    const showingCurrent = !!currentItems && viewState !== DEFAULT_VIEW;

    // searching a list you are not looking at would be confusing, so the controls go dead with it
    const controlsDisabled = disabled || showingCurrent;

    // initial load of the available list
    useEffect( () => { fetch( {} ); }, [] );

    function fetch( { searchText = search, currentPage = page, sortField = sort, sortDirection = direction } ) {
        loadSearch( { searchText, currentPage, sortField, sortDirection, limit: pageSize } );
    }

    const handleSearch = ( searchText ) => {
        setSearch( searchText );
        setPage( 1 );
        fetch( { searchText, currentPage: 1 } );
    };

    const handlePageChange = ( currentPage ) => {
        setPage( currentPage );
        fetch( { currentPage } );
    };

    const handleSortChange = ( sortField ) => {
        setSort( sortField );
        setPage( 1 );
        fetch( { currentPage: 1, sortField } );
    };

    const handleDirectionChange = ( sortDirection ) => {
        setDirection( sortDirection );
        setPage( 1 );
        fetch( { currentPage: 1, sortDirection } );
    };

    const renderSortMenu = () => (
        <div className="selector-table-sort-menu">
            <div className="selector-table-sort-label">Sort by</div>
            <Radio.Group value={ sort } onChange={ e => handleSortChange( e.target.value ) }>
                <Space direction="vertical">
                    <Radio value="name">Name</Radio>
                    <Radio value="updatedAt">Updated At</Radio>
                    <Radio value="createdAt">Created At</Radio>
                </Space>
            </Radio.Group>

            <Divider className="selector-table-sort-divider" />

            <div className="selector-table-sort-label">Direction</div>
            <Radio.Group value={ direction } onChange={ e => handleDirectionChange( e.target.value ) }>
                <Space direction="vertical">
                    <Radio value="ascend">Ascending</Radio>
                    <Radio value="descend">Descending</Radio>
                </Space>
            </Radio.Group>
        </div>
    );

    return <ItemTable
        { ...itemProps }
        type={ type }
        title={ showingCurrent ? currentLabel : title }
        disabled={ disabled }
        items={ showingCurrent ? currentItems : searchData }
        loading={ searchLoading && !showingCurrent }
        onTableChange={ pagination => pagination?.current && handlePageChange( pagination.current ) }
        controls={
            <div className='controls'>
                <Input.Search
                    allowClear
                    disabled={ controlsDisabled }
                    className="selector-table-search"
                    placeholder="Search"
                    onSearch={ handleSearch }
                />
                <Dropdown disabled={ controlsDisabled } trigger={ [ 'click' ] } popupRender={ renderSortMenu }>
                    <Button size="small" disabled={ controlsDisabled } icon={ <CaretDownOutlined /> }>
                        Sort
                    </Button>
                </Dropdown>
            </div>
        }
        footer={ <>
            { currentItems && <Radio.Group
                options={ [
                    { label: title, value: DEFAULT_VIEW },
                    { label: currentLabel + ( currentItems.length > 0 ? ` (${ currentItems.length } items)` : '' ), value: 'current' }
                ] }
                onChange={ e => setViewState( e.target.value ) }
                value={ viewState }
                disabled={ disabled }
                optionType="button"
                buttonStyle="solid"
            /> }

            <Pagination
                current={ page }
                pageSize={ pageSize }
                total={ searchDataCount }
                showSizeChanger={ false }
                showQuickJumper={ false }
                size={ 'small' }
                disabled={ controlsDisabled }
                onChange={ handlePageChange }
            />
        </> }
    />;
};

/**
 * Reorderable table: the whole value on one page, dragged to reorder, with the optional
 * disable toggle. After the initial id lookup it never queries again - every change is local and
 * goes straight back out through onChange as a whole new value.
 *
 * @param {Array} props.value the items this table shows and reorders
 * @param {boolean} props.reorder rows can be dragged
 * @param {boolean} props.hideable rows can be disabled rather than removed
 */
const Draggable = ( { type = 'objects', title = 'Content', value = [], onChange, reorder = true,
    hideable = false, disabled = false, hydrateIds, ...itemProps } ) => {

    // Latest value, readable from stale in-cell/in-row handler closures. rc-table memoizes rows by
    // record identity, so a cell/row built in an earlier render can fire an old handler; reading
    // through this ref keeps it current.
    const valueRef = useRef( value );
    valueRef.current = value;

    const [ showHidden, setShowHidden ] = useState( true );

    // Drag-to-reorder; commits the new order. Keyed on id (not the display index, which is
    // filtered) and resolved against the raw value, never the hydrated rows.
    const moveRow = ( fromId, toId ) => {
        if ( fromId === toId ) {
            return;
        }
        const next = [ ...valueRef.current ];
        const from = next.findIndex( i => i.id === fromId );
        const to = next.findIndex( i => i.id === toId );
        if ( from === -1 || to === -1 ) {
            return;
        }
        const [ moved ] = next.splice( from, 1 );
        next.splice( to, 0, moved );
        onChange?.( next );
    };

    const onHiddenChange = ( item, hidden ) => {
        onChange?.( valueRef.current.map( i => i.id === item.id ?
            { ...i, isEnabled: !hidden, sortOrder: null } : i ) );
    };

    const table = <ItemTable
        { ...itemProps }
        type={ type }
        title={ title }
        disabled={ disabled }
        items={ value }
        hydrateIds={ hydrateIds ?? value.map( i => i.id ) }
        hideable={ hideable }
        hideHidden={ !showHidden }
        onHiddenChange={ onHiddenChange }
        draggable={ reorder && !disabled }
        moveRow={ moveRow }
        controls={ hideable && <div className='controls'>
            <Checkbox disabled={ disabled } checked={ showHidden } onChange={ () => setShowHidden( !showHidden ) }>
                Show disabled items
            </Checkbox>
        </div> }
    />;

    // Reuse an ancestor DnD backend (hoisted to the app root) if one exists; only mount our own
    // when rendered standalone. react-dnd v11 throws "Cannot have two HTML5 backends at the same
    // time" if two mount at once.
    const { dragDropManager } = useContext( DndContext );

    return dragDropManager ? table : <DndProvider backend={ HTML5Backend }>{ table }</DndProvider>;
};

ItemTable.Searchable = Searchable;
ItemTable.Draggable = Draggable;

export { ItemTable };

/**
 * Two-pane transfer: browse on the left, the ordered selection on the right. It owns the value and
 * the checkbox state that the arrow buttons move between the panes; each pane owns everything else.
 */
const SelectorTable = ( {
    type = 'objects', renderRow, ref,
    onChange, value = [], // value represents the actual objects, not just the ids

    disabled = false,       // table is disabled
    hideable = false,       // selected items are hideable
    reorder = false,        // selected items are reorderable
    additionalColumns = [], // extra columns for data

    disabledIds = [],       // ids that should show as disabled - not-interactable, greyed
    uncheckableIds = [],    // ids that should not be checkable

    selectedTableStyle = {}, availableTableStyle = {} // style props
} ) => {

    // checked in this case means checked by the user - value will not be part of it
    const [ checkedAvailable, setCheckedAvailable ] = useState( [] );
    const [ checkedSelected, setCheckedSelected ] = useState( [] );

    useImperativeHandle( ref, () => ( {
        reset: () => {
            setCheckedAvailable( [] );
            setCheckedSelected( [] );
        }
    } ) );

    const commitChange = ( next ) => {
        onChange?.( next.map( ( item, i ) => ( { ...item, sortOrder: i } ) ) );
    };

    // Select-and-move (checkbox + arrow buttons).
    const handleTransferRight = () => {
        const newItems = checkedAvailable.map( a => ( { ...a, isEnabled: true } ) );
        commitChange( [ ...value, ...newItems ] );
        setCheckedAvailable( [] );
    };
    const handleTransferLeft = () => {
        commitChange( value.filter( s => !checkedSelected.some( i => i.id == s.id ) ) );
        setCheckedSelected( [] );
    };

    function addRemoveItems( records, add, setFn ) {
        setFn( prev => {
            if ( add ) {
                return dedupe( [ ...prev, ...records ], item => item.id );
            } else {
                return prev.filter( c => !records.find( r => r.id === c.id ) );
            }
        } );
    }

    // The following are just used for display states and are not necessarily used for data
    const availableCheckedItems = useMemo( () => dedupe( [ ...checkedAvailable, ...value ], item => item.id ), [ checkedAvailable, value ] );
    const availableDisabledIds = useMemo( () => dedupe( [ ...disabledIds, ...value.map( i => i.id ) ] ), [ disabledIds, value ] );
    const selectedPermanentIds = useMemo( () => value.filter( i => i.isPermanent ).map( i => i.id ), [ value ] );
    const valueIds = useMemo( () => value.map( i => i.id ), [ value ] );

    return (
        <div className={ 'selector-table' }>
            <div className="selector-table-columns">

                { /* Available Items
                * paginated content server-side, controlled by search and sort
                * not draggable
                * uncheckable: self item
                * disabled: self item, selected item
                */ }
                <ItemTable.Searchable
                    type={ type }
                    disabled={ disabled }
                    renderRow={ renderRow }
                    additionalColumns={ additionalColumns }
                    hydrateIds={ valueIds }
                    className="available-pane"
                    checkedItems={ availableCheckedItems }
                    disabledIds={ availableDisabledIds }
                    uncheckableIds={ uncheckableIds }
                    style={ availableTableStyle }
                    currentItems={ checkedAvailable }
                    onCheckedChange={ ( records, areSelected ) => addRemoveItems( records, areSelected, setCheckedAvailable ) }
                />

                <Space direction="vertical" className="selector-table-controls">
                    <Button
                        size="small"
                        type={ checkedAvailable.length ? 'primary' : 'default' }
                        disabled={ disabled || checkedAvailable.length === 0 }
                        onClick={ handleTransferRight }
                        icon={ <RightOutlined /> }
                    />
                    <Button
                        size="small"
                        type={ checkedSelected.length ? 'primary' : 'default' }
                        disabled={ disabled || checkedSelected.length === 0 }
                        onClick={ handleTransferLeft }
                        icon={ <LeftOutlined /> }
                    />
                </Space>

                { /* Selected Items
                * all items (non-paginated)
                * draggable sorting
                * hidden items sink in list
                * uncheckable: saved items
                * hidden: saved as hidden
                */ }
                <ItemTable.Draggable
                    type={ type }
                    value={ value }
                    onChange={ commitChange }
                    reorder={ reorder }
                    hideable={ hideable }
                    style={ selectedTableStyle }
                    className="selected-pane"
                    disabled={ disabled }
                    renderRow={ renderRow }
                    additionalColumns={ additionalColumns }
                    checkedItems={ checkedSelected }
                    disabledIds={ disabledIds }
                    uncheckableIds={ selectedPermanentIds }
                    onCheckedChange={ ( records, areSelected ) => addRemoveItems( records, areSelected, setCheckedSelected ) }
                />
            </div>
        </div>
    );
};

/* -------------------------------------------------------------------------- */
/* Per-type variants (compound components)                                    */
/* -------------------------------------------------------------------------- */

const LearningObjects = ( props ) => {

    const addtlColumns = useMemo( () => [
        {
            key: 'type',
            title: 'Type',
            dataIndex: 'format',
            render: ( format, item ) => {
                if ( item.placeholder ) { return <></>; }

                // a value item carries no format until the panes' id lookup lands
                return format ? <ColoredTag value={ format }>{ format.toUpperCase() }</ColoredTag> : <LoadingOutlined spin />;
            }
        }
    ], [] );

    return <SelectorTable
        { ...props }
        hideable
        type="objects"
        reorder
        additionalColumns={ addtlColumns }
    />;
};

const Resources = ( props ) => {

    const addtlColumns = useMemo( () => [
        {
            key: 'fileType',
            title: 'Type',
            dataIndex: 'localizations',
            align: 'center',
            width: 60,
            render: ( localizations, item ) => {
                if ( item.placeholder ) { return <></>; }

                // a value item carries no localizations until the panes' id lookup lands
                if ( localizations === undefined ) { return <></>; }

                // the file lives on the localization - use the default one, falling back to the first
                const localization = localizations?.find( l => l.isDefault ) ?? localizations?.[ 0 ];
                const file = localization?.resourceFile?.genericFile ?? localization?.resourceFile?.lxpFile ?? localization?.resourceUrl;
                const { name, color, icon } = getFileTypeSpecs( file );

                return <Tooltip title={ name }><span style={ { color } }>{ icon }</span></Tooltip>;
            }
        },
        {
            key: 'status',
            title: 'Status',
            dataIndex: 'isPublished',
            align: 'center',
            width: 60,
            render: ( isPublished, item ) => {
                if ( item.placeholder ) { return <></>; }

                // a value item carries no publish state until the panes' id lookup lands
                if ( isPublished === undefined ) { return <LoadingOutlined spin />; }

                return <Tooltip title={ isPublished ? 'Published' : 'Unpublished' }><ColoredTag.Publish isPublished={ isPublished } mini /></Tooltip>;
            }
        }
    ], [] );

    return <SelectorTable
        { ...props }
        type="resources"
        selectedTableStyle={ { marginTop: 50 } }
        additionalColumns={ addtlColumns }
    />;
};

SelectorTable.LearningObjects = LearningObjects;
SelectorTable.Resources = Resources;

export default SelectorTable;
